import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { ScrollReveal } from '#app/components/scroll-reveal.tsx'
import { Title } from './title.tsx'

// Auto-scroll speed for the testimonials marquee, in pixels per animation frame
// (~60fps). Higher = faster drift; set to 0 to disable the automatic scroll.
const AUTO_SCROLL_SPEED = 0.3

type Testimonial = {
	quote: string
	name: string
	role: string
	firm?: string
}

let testimonials: Testimonial[] = [
	{
		quote:
			'Como abogado litigante y corporativo, antes dedicaba horas interminables a la revisión manual para resumir casos y buscar sentencias aplicables. Al integrar IUS PRO a mi flujo de trabajo, logré agilizar drásticamente el resumen de expedientes complejos y realizar un análisis profundo de la jurisprudencia más actualizada. Lo he aplicado con éxito para fundamentar desde cero procesos sucesorios, penales y de cobro. Lo que más cambió mi día a día fue la seguridad y la velocidad: ahora arranco cualquier proceso con un fundamento legal robusto en una fracción del tiempo.',
		name: 'Spectrum Morales',
		role: 'Abogado litigante y corporativo',
		firm: 'R+ Studio Legal',
	},
	{
		quote:
			'Esta herramienta es excelente: me ayuda y me orienta. Estoy tan complacida que la he recomendado con muchos colegas. Me ahorra mucho tiempo, me ayuda a montar demandas muy rápido, me consigue jurisprudencia actualizada y me orienta con mayor objetividad cuando no sé por dónde empezar. La verdad, es una gran herramienta.',
		name: 'Natalia Sarmiento',
		role: 'Abogada y Notaria',
	},
	{
		quote:
			'En la búsqueda de opciones de inteligencia artificial para derecho me encontré esta opción y ha sido realmente una bendición. Es muy claro y accesible para el usuario, y sus resultados son muy acertados. Como toda IA hay que verificar, pero los resultados que me ha dado son excelentes. Es de las pocas cosas que pago y siento que recibo un beneficio real que justifica su pago.',
		name: 'Olman Aguilar C., MBA',
		role: 'Notario Público',
		firm: 'Despacho Legal 108 S.A.',
	},
	{
		quote:
			'La herramienta facilita y agiliza la búsqueda de información, y permite organizar el trabajo de una manera más eficiente, ordenada y rápida. Permite identificar, clasificar y analizar con mayor precisión los elementos relevantes, contribuyendo a una mejor comprensión técnica del caso y a una adecuada gestión de la información disponible.',
		name: 'José Antonio Reyes Villalobos',
		role: 'Abogado litigante',
		firm: 'AXIOMA',
	},
	{
		quote:
			'Puedo dar fe de que es un sistema muy versátil para la investigación jurídica. Da sugerencias y normativa acordes con el tema que se investiga. Además, la remisión a la jurisprudencia de Nexus del Poder Judicial facilita mucho la tarea. ¡Lo recomiendo!',
		name: 'Luis Alberto Varela Campo',
		role: 'Abogado y Notario',
	},
	{
		quote:
			'IUS PRO ha transformado la forma en que realizo mis investigaciones jurídicas. Hoy encuentro jurisprudencia y normativa ajustadas a mis necesidades de forma ágil y precisa. Lo que antes me tomaba varias horas, ahora lo obtengo en cuestión de minutos.',
		name: 'Marian Vargas',
		role: 'Abogada',
	},
	{
		quote:
			'Ha potenciado mi conocimiento y mis 26 años de experiencia, permitiéndome crear excelentes escritos jurídicos y ahorrar horas de trabajo. Más que satisfecho con lo que IUS PRO ha aportado a mi práctica.',
		name: 'Ricardo Alberto',
		role: 'Abogado',
	},
	{
		quote:
			'Facilita enormemente la labor diaria: la consulta de legislación, jurisprudencia y más. Lo que antes tomaba horas ahora toma mucho menos, con la ventaja de que refiere específicamente legislación y jurisprudencia de nuestro país. Definitivamente la recomiendo.',
		name: 'Jafeth López',
		role: 'Abogado Representante',
	},
	{
		quote:
			'Antes me tomaba mucho tiempo redactar documentos y debía transcribir montones de información. Cuando empecé a usar IUS PRO, noté que los tiempos de redacción bajaron considerablemente.',
		name: 'César Antonio Reyes Carvajal',
		role: 'Asesor Legal',
		firm: 'Municipalidad',
	},
	{
		quote:
			'El apoyo de esta IA es importante: me permite analizar en tiempo récord las consultas que se suscitan, con base en la normativa y la jurisprudencia. El ejercicio es enriquecedor y permite la emisión de criterios muy bien fundamentados.',
		name: 'Elizarda Vargas Morúa',
		role: 'Asesora Académica',
	},
]

export function initials(name: string) {
	return name
		.replace(/,.*$/, '')
		.split(' ')
		.filter(Boolean)
		.slice(0, 2)
		.map(word => word[0])
		.join('')
		.toUpperCase()
}

function TestimonialCard({ t, duplicate }: { t: Testimonial; duplicate?: boolean }) {
	return (
		<figure
			aria-hidden={duplicate || undefined}
			className="mr-5 flex w-[300px] shrink-0 flex-col rounded-3xl border border-neutral-800 bg-neutral-900/40 p-8 sm:mr-6 sm:w-[360px] lg:w-[400px] lg:p-10"
		>
			<span aria-hidden className="block text-5xl leading-none text-[#c9a962]/40">
				&ldquo;
			</span>
			<blockquote className="mt-3 text-[15px] leading-relaxed text-neutral-300">{t.quote}</blockquote>
			<figcaption className="mt-auto flex items-center gap-4 border-t border-neutral-800 pt-6">
				<div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#c9a962]/20 bg-[#c9a962]/10 text-sm font-medium text-[#c9a962]">
					{initials(t.name)}
				</div>
				<div className="min-w-0">
					<p className="truncate font-medium text-white">{t.name}</p>
					<p className="truncate text-sm text-neutral-500">
						{t.role}
						{t.firm ? ` · ${t.firm}` : ''}
					</p>
				</div>
			</figcaption>
		</figure>
	)
}

export function Testimonials() {
	let scrollRef = useRef<HTMLDivElement>(null)
	let jumpRef = useRef(0)

	// Arrow clicks queue a smooth, eased advance of ~30% of the visible width.
	function advance(direction: 1 | -1) {
		let node = scrollRef.current
		if (!node) return
		jumpRef.current += direction * node.clientWidth * 0.3
	}

	useEffect(() => {
		if (!scrollRef.current) return
		const node = scrollRef.current

		let reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
		let frame = 0
		let active = false // auto-scroll engaged (in view + delay elapsed)
		let startTimer: ReturnType<typeof setTimeout> | undefined

		function tick() {
			let half = node.scrollWidth / 2
			let delta = active && !reduce ? AUTO_SCROLL_SPEED : 0
			// Ease any queued arrow jump on top of the base drift.
			if (jumpRef.current !== 0) {
				let step = jumpRef.current * 0.12
				if (Math.abs(step) < 0.5) step = jumpRef.current
				jumpRef.current -= step
				delta += step
			}
			// Wrap into [0, half): the track holds two identical copies, so this stays
			// seamless for auto-scroll, the arrows and manual scrolling alike. Only write
			// when something actually changes so native momentum scrolling is preserved.
			if (half > 0) {
				let next = (((node.scrollLeft + delta) % half) + half) % half
				if (next !== node.scrollLeft) node.scrollLeft = next
			} else if (delta !== 0) {
				node.scrollLeft += delta
			}
			frame = requestAnimationFrame(tick)
		}
		frame = requestAnimationFrame(tick)

		let observer = new IntersectionObserver(
			([entry]) => {
				if (entry?.isIntersecting && !startTimer) {
					startTimer = setTimeout(() => {
						active = true
					}, 2000)
				}
			},
			{ threshold: 0.25 },
		)
		observer.observe(node)

		return () => {
			cancelAnimationFrame(frame)
			if (startTimer) clearTimeout(startTimer)
			observer.disconnect()
		}
	}, [])

	return (
		<section className="w-full overflow-hidden bg-neutral-950 py-32 text-white md:py-40">
			<div className="mx-auto max-w-7xl px-6 lg:px-8">
				<ScrollReveal className="mx-auto max-w-3xl text-center">
					<p className="text-sm font-medium uppercase tracking-widest text-[#c9a962]">Testimonios</p>
					<Title component="h2" className="mt-6 text-4xl text-white md:text-5xl">
						Lo que dicen los abogados que ya trabajan con IUS PRO.
					</Title>
					<p className="mx-auto mt-8 max-w-2xl text-lg leading-relaxed text-neutral-400">
						Abogados y notarios en litigio, notariado y derecho corporativo que usan IUS PRO en su práctica diaria.
					</p>
				</ScrollReveal>
			</div>

			{/* Full-bleed marquee: auto-scrolls after entering view, never stops. Slide manually or use the arrows. */}
			<div className="relative mt-20">
				<div ref={scrollRef} className="flex overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
					{[...testimonials, ...testimonials].map((t, i) => (
						<TestimonialCard key={i} t={t} duplicate={i >= testimonials.length} />
					))}
				</div>

				<button
					type="button"
					aria-label="Testimonios anteriores"
					onClick={() => advance(-1)}
					className="absolute inset-y-0 left-0 z-10 flex w-20 items-center justify-start bg-gradient-to-r from-neutral-950 via-neutral-950/70 to-transparent pl-3 text-neutral-400 transition-colors hover:text-white sm:w-24 lg:w-28"
				>
					<span className="flex h-10 w-10 items-center justify-center rounded-full border border-neutral-700 bg-neutral-900/80 backdrop-blur-sm">
						<ChevronLeft strokeWidth={1.5} className="h-5 w-5" />
					</span>
				</button>

				<button
					type="button"
					aria-label="Más testimonios"
					onClick={() => advance(1)}
					className="absolute inset-y-0 right-0 z-10 flex w-20 items-center justify-end bg-gradient-to-l from-neutral-950 via-neutral-950/70 to-transparent pr-3 text-neutral-400 transition-colors hover:text-white sm:w-24 lg:w-28"
				>
					<span className="flex h-10 w-10 items-center justify-center rounded-full border border-neutral-700 bg-neutral-900/80 backdrop-blur-sm">
						<ChevronRight strokeWidth={1.5} className="h-5 w-5" />
					</span>
				</button>
			</div>
		</section>
	)
}
